import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Grid, Pagination, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import styles from "./style";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import MiniDrawer from "../../Components/Drawer";
import BaseColor from "../../Config/Color";
import { getApiData } from "../../Apis/apiHelper";
import BaseSetting from "../../Apis/setting";
import _, { isArray, isEmpty } from "lodash";
import { createSearchParams, useSearchParams } from "react-router-dom";
import CSelect from "../../Components/CSelect";
import { downloadOptions } from "../../Config/staticData";
import Papa from "papaparse";

const WhatsAppTracking = () => {
    const searchRef = useRef();

    // loader
    const [pageLoad, setPageLoad] = useState(false);
    const [dataLoader, setDataLoader] = useState(false);

    // table data
    const [rows, setRows] = useState([]);

    // view details modal
    const [openView, setOpenView] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    // pagination and URL params (same style as Subscription table)
    const [pageNo, setPageNo] = useState(1);
    const [pagination, setPagination] = useState({ defaultPageSize: 25, totalPage: 1 });
    const [searchVal, setSearchVal] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectFileType, setSelectFileType] = useState("");

    const isPage = searchParams.get("page");
    const page = isPage > 1 ? Number(isPage) : 1;
    const sortVal = searchParams.has("sort") ? searchParams.get("sort") : "";
    const sortByVal = searchParams.has("sort_by") ? searchParams.get("sort_by") : "";
    const search = searchParams.has("search") ? searchParams.get("search") : "";

    const dataGridRef = useRef();
    const getDataGridApi = (api) => {
        dataGridRef.current = api;
    };

    const setHeaderParams = (page, search = "", sort = "", sortBy = "") => {
        const params = { page };
        setPageNo(page);
        if (!_.isEmpty(search)) {
            setSearchVal(search?.trim());
            params.search = search;
        }
        if (!_.isEmpty(sort)) params.sort = sort;
        if (!_.isEmpty(sortBy)) params.sort_by = sortBy;
        setSearchParams(createSearchParams(params));
    };

    const columns = useMemo(
        () => [
            {
                field: "actions",
                headerName: "ACTIONS",
                flex: 0.7,
                minWidth: 140,
                headerAlign: "center",
                align: "center",
                sortable: false,
                filterable: false,
                disableExport: true,
                renderCell: (params) => (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            setSelectedRow(params?.row || null);
                            setOpenView(true);
                        }}
                    >
                        View
                    </Button>
                ),
            },
            {
                field: "serial",
                headerName: "NO.",
                flex: 0.3,
                minWidth: 70,
                headerAlign: "center",
                align: "center",
                sortable: false,
                renderCell: (params) => {
                    const newStartIndex = (pageNo - 1) * (pagination?.defaultPageSize || 25) + 1;
                    rows.map((data, i) => {
                        data.serialNumber = i + newStartIndex;
                    });
                    return params?.row?.serialNumber || "-";
                },
            },
            { field: "username", headerName: "USERNAME", flex: 1, minWidth: 160, headerAlign: "center", align: "center" },
            { field: "mobileNumber", headerName: "MOBILE NUMBER", flex: 1, minWidth: 160, headerAlign: "center", align: "center" },
            { field: "email", headerName: "EMAIL", flex: 1.2, minWidth: 200, headerAlign: "center", align: "center" },
            { field: "country", headerName: "COUNTRY", flex: 0.8, minWidth: 120, headerAlign: "center", align: "center" },
            { field: "state", headerName: "STATE", flex: 0.8, minWidth: 120, headerAlign: "center", align: "center" },
            { field: "city", headerName: "CITY", flex: 0.8, minWidth: 120, headerAlign: "center", align: "center" },
            { field: "userType", headerName: "USER TYPE", flex: 0.8, minWidth: 120, headerAlign: "center", align: "center" },
            { field: "productName", headerName: "PRODUCT NAME", flex: 1.2, minWidth: 180, headerAlign: "center", align: "center" },
            { field: "category", headerName: "CATEGORY", flex: 0.9, minWidth: 160, headerAlign: "center", align: "center", sortable: false },
            { field: "subcategory", headerName: "SUBCATEGORY", flex: 0.9, minWidth: 160, headerAlign: "center", align: "center", sortable: false },
            { field: "categoryTwo", headerName: "CATEGORY TWO", flex: 0.9, minWidth: 160, headerAlign: "center", align: "center", sortable: false },
            { field: "categoryThree", headerName: "CATEGORY THREE", flex: 0.9, minWidth: 160, headerAlign: "center", align: "center", sortable: false },
            {
                field: "productPageLink",
                headerName: "PRODUCT PAGE LINK",
                flex: 1.2,
                minWidth: 200,
                headerAlign: "center",
                align: "center",
                renderCell: (params) => (
                    params.value ? (
                        <a href={params.value} target="_blank" rel="noreferrer">
                            Open
                        </a>
                    ) : (
                        "-"
                    )
                ),
            },
            { field: "clickedAt", headerName: "DATE & TIME", flex: 1, minWidth: 180, headerAlign: "center", align: "center" },
        ],
        [pageNo, pagination, rows]
    );

    const handleDownloadCSV = async (data) => {
        const csvData = Papa.unparse(data);
        const blob = new Blob([csvData], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "WhatsApp-Tracking-Data.csv";
        link.click();
        URL.revokeObjectURL(url);
        setSelectFileType("");
    };

    function getDownloadData(type) {
        const exportRows = (isArray(rows) ? rows : []).map((r, idx) => ({
            No: idx + 1,
            Username: r.username || "-",
            "Mobile Number": r.mobileNumber || "-",
            Email: r.email || "-",
            Country: r.country || "-",
            State: r.state || "-",
            City: r.city || "-",
            "User Type": r.userType || "-",
            "Product Name": r.productName || "-",
            Category: r.category || "-",
            Subcategory: r.subcategory || "-",
            "Category Two": r.categoryTwo || "-",
            "Category Three": r.categoryThree || "-",
            "Product Page Link": r.productPageLink || "-",
            "Date & Time": r.clickedAt || "-",
            "Seller Username": r.sellerUsername || "-",
            "Seller Mobile": r.sellerPhone || "-",
            "Seller Email": r.sellerEmail || "-",
            "Seller Country": r.sellerCountry || "-",
            "Seller State": r.sellerState || "-",
            "Seller City": r.sellerCity || "-",
        }));

        if (type === "Download as CSV") {
            handleDownloadCSV(exportRows);
        }
    }

    function mapListToRows(list, page) {
        const pageSize = pagination?.defaultPageSize || 25;
        const startIndex = (page - 1) * pageSize;

        const transformItem = (item, idx) => {
            const id = item.id || item._id || startIndex + idx + 1;
            // Prefer discrete category fields, fallback to a combined path string
            const categoryPath = item.productCategoriesPath || item.categoryPath || item.productCategories || "";
            const pathParts = typeof categoryPath === "string" ? categoryPath.split("<").map((s) => s.trim()).filter(Boolean) : [];

            const buyerPhone = item.buyer_phone_code && item.buyer_phone
                ? `+${item.buyer_phone_code} ${item.buyer_phone}`
                : item.buyer_phone || item.mobileNumber || item.phone || item.mobile || "";

            const sellerPhone = item.seller_phone_code && item.seller_phone
                ? `+${item.seller_phone_code} ${item.seller_phone}`
                : item.seller_phone || "";

            const category = item.category || pathParts[0] || "-";
            const subcategory = item.subcategory || pathParts[1] || "-";
            const categoryTwo = item.subcategoryTwo || pathParts[2] || "-";
            const categoryThree = item.subcategoryThree || pathParts[3] || "-";

            const clickedAtRaw = item.clicked_at || item.clickedAt || item.createdAt || item.dateTime || "";
            const clickedAt = clickedAtRaw ? new Date(clickedAtRaw).toLocaleString() : "-";

            return {
                id,
                // Table (buyer-facing) fields
                username: item.buyer_username || item.username || item.userName || "-",
                mobileNumber: buyerPhone || "-",
                email: item.buyer_email || item.email || "-",
                country: item.buyer_country || item.country || item.countryName || "-",
                state: item.buyer_state || item.state || item.stateName || "-",
                city: item.buyer_city || item.city || item.cityName || "-",
                userType: "Buyer",
                productName: item.product_name || item.productName || item.postTitle || item.name || "-",
                category,
                subcategory,
                categoryTwo,
                categoryThree,
                productPageLink: item.product_page_link || item.productPageLink || item.postUrl || item.url || "",
                clickedAt,

                // Buyer details (for dialog)
                buyerUsername: item.buyer_username || "-",
                buyerPhone: buyerPhone || "-",
                buyerEmail: item.buyer_email || "-",
                buyerCountry: item.buyer_country || "-",
                buyerState: item.buyer_state || "-",
                buyerCity: item.buyer_city || "-",

                // Seller details (for dialog)
                sellerUsername: item.seller_username || "-",
                sellerPhone: sellerPhone || "-",
                sellerEmail: item.seller_email || "-",
                sellerCountry: item.seller_country || "-",
                sellerState: item.seller_state || "-",
                sellerCity: item.seller_city || "-",
            };
        };

        const source = isArray(list) ? list : [];
        const normalized = source.map(transformItem);
        if (normalized.length > 0) return normalized;
        return [];
    }

    async function getWhatsAppTrackingData(page, search = "", sort = "", sortBy = "") {
        const payload = { page };
        if (!_.isEmpty(search)) payload.searchVal = search?.trim();
        if (!_.isEmpty(sort)) payload.sort = sort;
        if (!_.isEmpty(sortBy)) payload.sort_by = sortBy;

        setDataLoader(true);
        getApiData(BaseSetting.endpoint.whatsappTrackingList, "post", payload)
            .then((res) => {

                console.log("💥️~ getWhatsAppTrackingData ~ res ->", res);
                const response = res?.data || res || {};
                const dataObj = response?.data || response;
                const list = dataObj?.whatsappButtonClickData || dataObj?.list || dataObj?.rows || dataObj?.result || dataObj?.data || [];
                const transformed = mapListToRows(list, page);
                setRows(transformed);

                const pg = dataObj?.pagination || response?.pagination || {};
                const pageSize = pg?.limit || pg?.defaultPageSize || 25;
                const totalPage = pg?.totalPage || pg?.totalPages || 1;
                setPagination({ defaultPageSize: pageSize, totalPage });
            })
            .catch(() => {
                setRows([]);
                setPagination({ defaultPageSize: 25, totalPage: 1 });
            })
            .finally(() => {
                setDataLoader(false);
                setPageLoad(false);
            });
    }

    useEffect(() => {
        setPageLoad(true);
        setHeaderParams(page, search, sortVal, sortByVal);
        getWhatsAppTrackingData(page, search, sortVal, sortByVal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Grid container>
            <MiniDrawer
                header="WhatsApp Tracking"
                children={
                    <>
                        <Grid container justifyContent={"flex-end"}>
                            <div style={{ display: "flex" }}>
                                <CSelect
                                    style={{ marginLeft: "20px" }}
                                    placeholder="Choose file format for download"
                                    value={selectFileType}
                                    data={downloadOptions}
                                    onSelect={(val) => {
                                        setSelectFileType(val);
                                        getDownloadData(val);
                                    }}
                                />
                            </div>
                        </Grid>
                        <Grid container mt={"20px"}>
                            {pageLoad ? (
                                <div style={styles.loaderContainer}>
                                    <CircularProgress style={{ color: BaseColor.primary }} size={30} />
                                </div>
                            ) : (
                                <DataGrid
                                    sortModel={
                                        sortByVal === "asc" || sortByVal === "desc"
                                            ? [
                                                {
                                                    field: sortVal,
                                                    sort: sortByVal,
                                                },
                                            ]
                                            : []
                                    }
                                    onSortModelChange={(model) => {
                                        setDataLoader(true);
                                        if (!_.isEmpty(model)) {
                                            setHeaderParams(page, search, model[0]?.field, model[0].sort);
                                            getWhatsAppTrackingData(page, search, model[0]?.field, model[0].sort);
                                        } else {
                                            setHeaderParams(page, search, "", "");
                                            getWhatsAppTrackingData(page, search);
                                        }
                                    }}
                                    disableColumnFilter
                                    disableColumnSelector
                                    rows={rows}
                                    columns={columns}
                                    disableColumnMenu
                                    hideFooter={false}
                                    hideFooterPagination
                                    loading={dataLoader}
                                    sortingMode="server"
                                    apiRef={getDataGridApi}
                                    disableDensitySelector
                                    checkboxSelection={false}
                                    paginationMode="server"
                                    showCellVerticalBorder={true}
                                    disableRowSelectionOnClick
                                    showColumnVerticalBorder={true}
                                    disableSelectionOnClick
                                    autoHeight={true}
                                    getRowHeight={() => "auto"}
                                    slots={{ toolbar: GridToolbar }}
                                    initialState={{
                                        filter: {
                                            filterModel: {
                                                items: [],
                                                quickFilterValues: [searchVal],
                                            },
                                        },
                                    }}
                                    sx={styles.dataGridSx}
                                    slotProps={{
                                        toolbar: {
                                            printOptions: { disableToolbarButton: true },
                                            csvOptions: { disableToolbarButton: true },
                                            showQuickFilter: true,
                                            onChange: (e) => {
                                                setDataLoader(true);
                                                const val = e?.target?.value?.toLowerCase();
                                                setSearchVal(val);
                                                setHeaderParams(1, val, sortVal, sortByVal);
                                                if (searchRef.current) clearTimeout(searchRef.current);
                                                searchRef.current = setTimeout(() => {
                                                    getWhatsAppTrackingData(1, val, sortVal, sortByVal);
                                                }, 800);
                                            },
                                        },
                                    }}
                                />
                            )}
                        </Grid>
                        {!isEmpty(rows) && isArray(rows) && (
                            <div style={styles.paginationContainer}>
                                <Pagination
                                    count={pagination?.totalPage}
                                    page={page}
                                    siblingCount={2}
                                    onChange={(e, value) => {
                                        setPageLoad(true);
                                        setPageNo(value);
                                        setHeaderParams(value, search, sortVal, sortByVal);
                                        getWhatsAppTrackingData(value, search, sortVal, sortByVal);
                                    }}
                                    sx={{ display: pageLoad ? "none" : "block" }}
                                />
                            </div>
                        )}
                        <Dialog
                            open={openView}
                            onClose={() => setOpenView(false)}
                            maxWidth="lg"
                            fullWidth
                        >
                            <DialogTitle>WhatsApp Click Details</DialogTitle>
                            <DialogContent style={styles.dialogContent}>
                                {selectedRow && (
                                    <>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "start" }}>
                                            <div>
                                                <div style={{ fontWeight: 700, marginBottom: 8 }}>Buyer Details</div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Username</span>
                                                    <span style={styles.detailValue}>{selectedRow.buyerUsername || selectedRow.username || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Mobile</span>
                                                    <span style={styles.detailValue}>{selectedRow.buyerPhone || selectedRow.mobileNumber || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Email</span>
                                                    <span style={styles.detailValue}>{selectedRow.buyerEmail || selectedRow.email || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Country</span>
                                                    <span style={styles.detailValue}>{selectedRow.buyerCountry || selectedRow.country || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>State</span>
                                                    <span style={styles.detailValue}>{selectedRow.buyerState || selectedRow.state || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>City</span>
                                                    <span style={styles.detailValue}>{selectedRow.buyerCity || selectedRow.city || "-"}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 700, marginBottom: 8 }}>Seller Details</div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Username</span>
                                                    <span style={styles.detailValue}>{selectedRow.sellerUsername || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Mobile</span>
                                                    <span style={styles.detailValue}>{selectedRow.sellerPhone || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Email</span>
                                                    <span style={styles.detailValue}>{selectedRow.sellerEmail || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>Country</span>
                                                    <span style={styles.detailValue}>{selectedRow.sellerCountry || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>State</span>
                                                    <span style={styles.detailValue}>{selectedRow.sellerState || "-"}</span>
                                                </div>
                                                <div style={styles.detailRow}>
                                                    <span style={styles.detailLabel}>City</span>
                                                    <span style={styles.detailValue}>{selectedRow.sellerCity || "-"}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Product Info */}
                                        <div style={{ marginTop: 12 }}>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Product Name</span>
                                                <span style={styles.detailValue}>{selectedRow.productName || "-"}</span>
                                            </div>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Category</span>
                                                <span style={styles.detailValue}>{selectedRow.category || "-"}</span>
                                            </div>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Subcategory</span>
                                                <span style={styles.detailValue}>{selectedRow.subcategory || "-"}</span>
                                            </div>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Category Two</span>
                                                <span style={styles.detailValue}>{selectedRow.categoryTwo || "-"}</span>
                                            </div>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Category Three</span>
                                                <span style={styles.detailValue}>{selectedRow.categoryThree || "-"}</span>
                                            </div>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Product Page</span>
                                                <span style={styles.detailValue}>
                                                    {selectedRow.productPageLink ? (
                                                        <a href={selectedRow.productPageLink} target="_blank" rel="noreferrer">Open</a>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </span>
                                            </div>
                                            <div style={styles.detailRow}>
                                                <span style={styles.detailLabel}>Date & Time</span>
                                                <span style={styles.detailValue}>{selectedRow.clickedAt || "-"}</span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenView(false)}>Close</Button>
                            </DialogActions>
                        </Dialog>
                    </>
                }
            />
        </Grid>
    );
};

export default WhatsAppTracking;


